import * as React from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import { style, styled, useTheme, SxProps } from '@mui/system';
import { Link, Theme } from '@mui/material';
import { Setting } from '../../settings';

export interface RowData {
  id: number;
  date: string;
  status: string;
  amount: number;
}

const NUMBER_TABLE_WIDTH = '25px';

export const StyledTableRow = styled(TableRow)(() => {

  const theme = useTheme();

  return {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    }
  };

});

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export interface HeadCell {
  disablePadding: boolean;
  id: string;
  label: string;
  numeric: boolean;
  style?: any;
  width?: string | number,
  cellClickable?: boolean | string;
  customCellLink?: (cell: any, row: any) => string,
  customCell?: (cell: any) => JSX.Element,
}


interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  headCells: HeadCell[]
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { headCells, order, orderBy, numSelected, rowCount, onRequestSort } =
    props;
  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const theme = useTheme();

  return (
    <TableHead sx={{ backgroundColor: theme.palette.primary.main }}>
      <TableRow>
        {headCells.map((headCell: HeadCell) => (
          <TableCell
            key={headCell.id}
            sx={{ 
              color: theme.palette.common.white, 
              padding: theme.spacing(1,2), 
              width: headCell.width ? headCell.width : (headCell.numeric ? NUMBER_TABLE_WIDTH: 'auto') 
            }}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              sx={{ color: theme.palette.common.white, fontWeight: 400 }}
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default function PortalTable({ 
  data,
  headCells,
  pagination,
  onCellClick,
  rowStyle,
  defaultOrderBy,
  settings
}: {
    data: Array<any>, 
    headCells: HeadCell[], 
    pagination?: boolean,
    onCellClick?: (cell: HeadCell, value: any, row: any) => void,
    rowStyle?: (row: any) => any,
    defaultOrderBy?: string,
    settings?: Setting[]
  }) {
  
  if (pagination === undefined) {
    pagination = true;
  }

  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<string>(defaultOrderBy ? defaultOrderBy : 'date');
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);

  const theme = useTheme();

  const rows = data;

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleClick = (event: React.MouseEvent<unknown>, data: any) => {
    /* const selectedIndex = selected.indexOf(name);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);*/
  };

  const handleCellClick = (x: HeadCell, value: any, row: any) =>{

    if (x.cellClickable && onCellClick) {
      onCellClick(x, value, row);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer>
        <Table
          sx={{ minWidth: 750, }}
          aria-labelledby="tableTitle"
          size="small"
        >
          <EnhancedTableHead
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
            headCells={headCells}
          />
          <TableBody>
            {/* if you don't need to support IE11, you can replace the `stableSort` call with:
              rows.slice().sort(getComparator(order, orderBy)) */}
            {stableSort(rows, getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (<>
                  <StyledTableRow
                    hover
                    onClick={(event) => handleClick(event, row)}
                    tabIndex={-1}
                    key={row.id}
                  >
                    {headCells.map((x, i) => {
                      
                        const defaultStyle = { 
                        padding: (theme: Theme)=>theme.spacing(0.5, 1), 
                        width: x.width ? x.width : (x.numeric ? NUMBER_TABLE_WIDTH: 'auto'),
                        ...x.style
                      };

                      if (x.customCell) {
                        
                        const CustomCell = x.customCell;

                        return (<TableCell
                          onClick={()=>handleCellClick(x, row[x.id], row)} 
                          sx={rowStyle ? { ...defaultStyle, ...rowStyle(row) } : defaultStyle} 
                          key={i} 
                          align={x.numeric ? 'right' : 'left'}
                        >{CustomCell(row)}</TableCell>);
                      }
                      
                      return (<TableCell
                        onClick={()=>handleCellClick(x, row[x.id], row)} 
                        sx={rowStyle ? { ...defaultStyle, ...rowStyle(row) } : defaultStyle} 
                        key={i} 
                        align={x.numeric ? 'right' : 'left'}
                      >{x.cellClickable ? 
                          <Link 
                            href={typeof x.cellClickable === 'string' ? (x.customCellLink ? x.customCellLink(x, row) : x.cellClickable.replace(':value', row[x.id] as string)) : '#'}
                            onClick={(ev: React.MouseEvent)=>ev.preventDefault()}>
                            {row[x.id]}
                          </Link> : row[x.id]}
                      </TableCell>);
                    })}
                  </StyledTableRow>
                  {row.subRow ? <TableRow><TableCell style={{overflowX: 'scroll'}} colSpan={headCells.length}>{row.subRow}</TableCell></TableRow> : null}
                </>
                );
              })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: (dense ? 33 : 53) * emptyRows,
                }}
              >
                <TableCell colSpan={headCells.length} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination ? <TablePagination
        rowsPerPageOptions={[50, 100, 200]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      /> : null}
    </Box>
  );
}